import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import ContentLayout from "@cloudscape-design/components/content-layout"
import Header from "@cloudscape-design/components/header"
import SegmentedControl from "@cloudscape-design/components/segmented-control"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import { useState, useMemo, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Breadcrumb from "../../components/Navigation/Breadcrumb"
import NotificationCard from "../../components/Notifications/NotificationCard"
import Pagination from "../../components/Pagination/Pagination"
import { useAuth } from "../../contexts/AuthContext"
import {
  useAllNotifications,
  useNotificationSummaryWithTotal,
  useMarkNotificationsRead,
} from "../../hooks/queries/useNotifications"
import { usePageParam } from "../../hooks/usePageParam"

const ITEMS_PER_PAGE = 20

const NotificationsPage = () => {
  const { user } = useAuth()
  const daeId = user?.isAuthenticated ? user.principalId : undefined
  const markReadMutation = useMarkNotificationsRead(daeId)
  const markAsRead = (id: string) => markReadMutation.mutate([id])
  const navigate = useNavigate()
  const [filter, setFilterRaw] = useState<"all" | "unread" | "read">("all")
  const [currentPage, setCurrentPage] = usePageParam()

  const setFilter = (next: "all" | "unread" | "read") => {
    setFilterRaw(next)
    setCurrentPage(1)
  }

  const {
    data,
    isLoading: loading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useAllNotifications(daeId)

  const { data: summary, isError: summaryError } = useNotificationSummaryWithTotal(daeId)

  // Load every page once so the full list + "mark all as read" cover all notifications.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const allNotifications = useMemo(() => data?.pages.flatMap((p) => p.notifications) ?? [], [data])

  const sortedNotifications = useMemo(
    () =>
      [...allNotifications].sort(
        (a, b) => new Date(b.sent_timestamp).getTime() - new Date(a.sent_timestamp).getTime(),
      ),
    [allNotifications],
  )

  // Exact server counts from the with-total summary query - no "+" estimates.
  const totalCount = summary?.totalCount ?? 0
  const unreadCount = summary?.unreadCount ?? 0
  // unread and total come from separate (non-atomic) count queries, so a notification landing
  // between them can briefly make unread > total; floor the derived read count at 0.
  const readCount = Math.max(0, totalCount - unreadCount)

  const markAllAsRead = () => {
    const unreadIds = sortedNotifications
      .filter((n) => n.read_status === "UNREAD")
      .map((n) => n.notification_id)
    if (unreadIds.length > 0) markReadMutation.mutate(unreadIds)
  }

  const filteredNotifications = useMemo(() => {
    const filtered = sortedNotifications.filter((n) => {
      if (filter === "unread") return n.read_status === "UNREAD"
      if (filter === "read") return n.read_status === "READ"
      return true
    })
    return filtered
  }, [sortedNotifications, filter])

  const pageCount = Math.max(1, Math.ceil(filteredNotifications.length / ITEMS_PER_PAGE))
  const paginatedNotifications = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredNotifications.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredNotifications, currentPage])

  const canGoNext = currentPage < pageCount
  const handleNextPage = () => {
    if (canGoNext) setCurrentPage(currentPage + 1)
  }
  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }

  if (!user) {
    navigate("/login")
    return null
  }

  const isUnread = filter === "unread"

  const emptyTitle = isUnread
    ? "No unread notifications"
    : filter === "read"
      ? "No read notifications"
      : "No notifications"

  const emptyDescription = isUnread
    ? "You're all caught up!"
    : "Notifications will appear here when there are updates."

  const filterOptions = [
    { id: "all", text: `All (${totalCount})` },
    { id: "unread", text: `Unread (${unreadCount})` },
    {
      id: "read",
      text: `Read (${readCount})`,
    },
  ]

  const showList = !(loading && allNotifications.length === 0) && filteredNotifications.length > 0

  return (
    <>
      <Breadcrumb
        items={[{ label: "Dashboard", path: "/dae/dashboard" }, { label: "Notifications" }]}
      />
      <ContentLayout
        header={
          <Header
            variant="h1"
            description="Stay updated on your study activities"
            actions={unreadCount > 0 && <Button onClick={markAllAsRead}>Mark All as Read</Button>}
          >
            Notifications
          </Header>
        }
      >
        <Container
          footer={
            showList &&
            (currentPage > 1 || canGoNext) && (
              <Box float="right">
                <Pagination
                  currentPage={currentPage}
                  pageCount={pageCount}
                  hasMore={hasNextPage}
                  loading={loading || isFetchingNextPage}
                  onPageChange={setCurrentPage}
                  onNext={handleNextPage}
                  onPrevious={handlePrevPage}
                />
              </Box>
            )
          }
        >
          <SpaceBetween size="m">
            {summaryError && (
              <Alert type="error" header="Couldn't load notification counts">
                The unread/total counts may be out of date. Try refreshing the page.
              </Alert>
            )}
            <SegmentedControl
              selectedId={filter}
              onChange={({ detail }) => setFilter(detail.selectedId as typeof filter)}
              label="Filter notifications"
              options={filterOptions}
            />

            {loading && allNotifications.length === 0 ? (
              <Box textAlign="center" padding="xxl" color="text-status-inactive">
                <SpaceBetween size="s" alignItems="center">
                  <Spinner size="large" />
                  <span>Loading notifications...</span>
                </SpaceBetween>
              </Box>
            ) : filteredNotifications.length === 0 ? (
              <Box textAlign="center" padding="xxl" color="text-status-inactive">
                <SpaceBetween size="xs">
                  <Box variant="strong" color="inherit">
                    {emptyTitle}
                  </Box>
                  <span>{emptyDescription}</span>
                </SpaceBetween>
              </Box>
            ) : (
              <SpaceBetween size="s">
                {paginatedNotifications.map((notification) => (
                  <NotificationCard
                    key={notification.notification_id}
                    notification={notification}
                    onMarkAsRead={markAsRead}
                  />
                ))}
              </SpaceBetween>
            )}
          </SpaceBetween>
        </Container>
      </ContentLayout>
    </>
  )
}

export default NotificationsPage
